import * as THREE from 'three';
import { G, mesh } from '../render/scene';
import { MAT_P, coreGlow } from '../render/materials';
import { bevel, joint, mark, plate, ram, rib, seam } from './mech-kit';
import { LPOSE } from './lancer-pose';
/* ============================================================
   LANCER-2 · 主武裝 MAIN（右臂持刃）與掛架 PYLON（左臂持盾）
   ============================================================

   CHASSIS-0 的手臂是「拿槍的架子」：細、直、末端是砲管。
   這一台的手臂**本身就是武器的一部分**，所以整條都要有肉 ——
   上臂與前臂都用三段紡錘（上段接關節、中段最鼓、下段收進關節），
   與腿同一個作法。前臂特別粗：那是近戰機體視線最先落到的地方。

   刃掛在 `aim` 群組下方朝下延伸，與 CHASSIS-0 的槍口同一個座標約定，
   所以 `mech-fx.ts` 的槍口閃光（`rig.muzzleY`）不用改就能當**刃芒**用。

   左邊在 CHASSIS-0 是被動掛架，這一台是**整條左臂 + 盾**：近戰機體
   要有東西擋在身前，不然貼身時剪影只剩「一根往前伸的刃」。 */

function lancerArmBase(g) {
  g.add(joint(0.28, [0, 0, 0], true));                                          // 肩關節（粗、軸端外露）
  var up = new THREE.Group(); up.userData.bone = 1;
  up.rotation.set(LPOSE.armPitch, LPOSE.armYaw, LPOSE.armRoll);
  g.add(up);
  up.add(bevel(MAT_P.dark, 0.40, 0.26, 0.40, [0, -0.16, 0]));                    // 上臂上段
  up.add(bevel(MAT_P.dark, 0.46, 0.30, 0.44, [0, -0.44, 0]));                    // 上臂中段（最鼓）
  up.add(bevel(MAT_P.dark, 0.34, 0.26, 0.34, [0, -0.72, 0]));                    // 上臂下段（收進肘）
  up.add(ram([0.22, -0.56, -0.17], 0.54, 0.10, 0.085, [0.12, 0, 0]));            // 肘液壓（後方）
  /* 肩甲拆成**兩層小板**而不是一片大板。一片 0.86×0.92 的板子在側面
     鏡頭下是一面正對光的方形亮牆，把整個上半身吃掉（實拍：
     shots/lancer/iso-lancer2-arm.png 關掉手臂就不見了）。
     兩層錯開之後有邊、有影、有第二條亮線，同樣的體積讀成裝甲而不是板子。 */
  up.add(plate(MAT_P.armor, 0.24, 0.62, 0.76, [-0.42, 0.06, 0], 0.14, -0.20, 0.32));  // 肩甲第一層
  up.add(plate(MAT_P.armor, 0.18, 0.42, 0.54, [-0.55, 0.24, 0.06], 0.14, -0.20, 0.32));// 第二層（外上）
  up.add(rib(MAT_P.armor, 0.10, 0.10, 0.76, [-0.50, 0.36, 0], 0.14, -0.20, 0.32));    // 上緣亮線
  up.add(seam(0.06, 0.54, 0.10, [-0.46, 0.04, 0.26], 0.14, -0.20, 0.32));

  var el = new THREE.Group(); el.position.set(0, -0.90, 0); up.add(el);
  el.add(joint(0.22, [0, 0, 0]));
  var fo = new THREE.Group(); fo.userData.bone = 1;
  fo.rotation.x = LPOSE.forePitch; el.add(fo);
  var aim = new THREE.Group(); aim.userData.bone = 1;
  aim.rotation.z = LPOSE.aimConverge; fo.add(aim);
  g.userData.rig = { up: up, fore: fo, aim: aim };
  return aim;
}

export function buildLancerBlade(shape, P?) {
  P = P || { glow: coreGlow('core-std') };
  var g = new THREE.Group();
  mark(g, shape, ['main-pierce', 'main-chain', 'main-vulcan']);
  var fo = lancerArmBase(g);
  /* 刃尖在前臂座標的 y。**這也是槍口閃光的掛點**（`render/mech-fx.ts`
     讀 `rig.muzzleY`），所以近戰的「刃芒」不用另外做一套特效。
     第一輪拍出來刃長 2.6 —— 比機體全高還長，整個畫面只剩一把刀，
     現在收到 1.8 以內（約前臂的 1.4 倍）。 */
  g.userData.muzzle = shape === 'main-pierce' ? -2.10 : shape === 'main-chain' ? -1.60 : -1.80;

  // 前臂（三段紡錘）：三種刃共用，差別只在刃本身
  fo.add(bevel(MAT_P.dark, 0.42, 0.26, 0.42, [0, -0.16, 0]));
  fo.add(bevel(MAT_P.dark, 0.48, 0.32, 0.46, [0, -0.44, 0]));                    // 前臂最鼓處
  fo.add(bevel(MAT_P.dark, 0.34, 0.26, 0.34, [0, -0.74, 0]));
  fo.add(plate(MAT_P.armor, 0.52, 0.70, 0.44, [0, -0.40, 0.16], 0));             // 前臂外甲
  fo.add(rib(MAT_P.armor, 0.08, 0.74, 0.08, [0.22, -0.40, 0.20], 0));
  fo.add(rib(MAT_P.armor, 0.08, 0.74, 0.08, [-0.22, -0.40, 0.20], 0));
  fo.add(mesh(G.box, MAT_P.dark, [0.38, 0.34, 0.28], [0, -0.86, -0.28]));        // 刃座配重

  if (shape === 'main-pierce') {
    /* 穿刺刃：長、細、單尖。剪影上是一根從肩延伸出去的直線 —— 三種刃裡
       攻擊距離讀起來最遠的那一種。 */
    fo.add(mesh(G.cyl, MAT_P.trim, [0.20, 0.44, 0.20], [0, -1.06, 0]));
    fo.add(mesh(G.bev, MAT_P.armor, [0.24, 0.66, 0.24], [0, -1.44, 0]));
    fo.add(mesh(G.box, P.glow, [0.06, 0.80, 0.11], [0, -1.50, 0]));
    fo.add(mesh(G.cone, P.glow, [0.20, 0.46, 0.20], [0, -2.02, 0], [Math.PI, 0, 0]));
    fo.add(mesh(G.tor, MAT_P.trim, [0.32, 0.32, 0.14], [0, -1.12, 0], [Math.PI / 2, 0, 0]));

  } else if (shape === 'main-chain') {
    /* 鏈鋸刃：短、寬、多節。近身連打的那一把，讀起來是「削」不是「劈」。 */
    fo.add(mesh(G.box, MAT_P.trim, [0.30, 0.36, 0.34], [0, -1.02, 0.02]));
    fo.add(mesh(G.box, MAT_P.armor, [0.20, 0.62, 0.44], [0, -1.36, 0.04]));
    for (var i = 0; i < 3; i++)
      fo.add(mesh(G.box, P.glow, [0.24, 0.09, 0.13], [0, -1.14 - i * 0.20, 0.26]));  // 鋸齒
    fo.add(mesh(G.cone, MAT_P.armor, [0.24, 0.28, 0.42], [0, -1.72, 0.04], [Math.PI, 0, 0]));
    fo.add(rib(MAT_P.armor, 0.09, 0.64, 0.09, [0, -1.36, -0.18], 0));

  } else {  /* main-vulcan：標準電磁刃 —— 單片長刃，刃身整條發光 */
    fo.add(mesh(G.box, MAT_P.trim, [0.32, 0.32, 0.36], [0, -1.02, 0.04]));       // 刃柄
    fo.add(mesh(G.box, MAT_P.armor, [0.11, 0.86, 0.42], [0, -1.50, 0.06]));      // 刃脊
    /* 發光只給刃**緣**一條，而且要窄。整片發光在機庫的近距離鏡頭下會過曝，
       但光收窄還不夠 —— **bloom 會把細長亮面糊成一團**，第二輪拍出來
       整條前臂是一坨白（shots/hangar-mech/）。0.09 寬 × 0.62 長是實拍收斂值。 */
    fo.add(mesh(G.box, P.glow, [0.05, 0.62, 0.09], [0, -1.52, 0.26]));           // 刃緣（發光）
    fo.add(mesh(G.cone, P.glow, [0.19, 0.26, 0.09], [0, -2.02, 0.18], [Math.PI, 0, 0])); // 刃尖
    fo.add(mesh(G.box, MAT_P.trim, [0.20, 0.11, 0.42], [0, -1.14, 0.20]));       // 護手
  }
  return g;
}

/* 掛架 PYLON：左臂（+X）+ 盾。`mech-anim.ts` 的開架動畫在這裡讀成
   「舉盾」——同一個彈簧，語意剛好對得上，不用另外做一層。 */
export function buildLancerShield(shape, P?) {
  P = P || { glow: coreGlow('core-std') };
  var g = new THREE.Group();
  mark(g, shape, ['pyl-weapon', 'pyl-passive', 'pyl-std']);
  g.add(joint(0.26, [0, 0, 0], true));
  g.add(bevel(MAT_P.dark, 0.38, 0.26, 0.38, [0.06, -0.16, 0]));                  // 上臂（短，被盾遮住）
  g.add(bevel(MAT_P.dark, 0.44, 0.30, 0.42, [0.10, -0.44, 0]));
  g.add(bevel(MAT_P.dark, 0.32, 0.34, 0.32, [0.12, -0.76, 0]));
  g.add(ram([0.30, -0.58, -0.16], 0.52, 0.10, 0.08, [0.12, 0, 0]));

  if (shape === 'pyl-weapon') {
    /* 武裝掛架：盾面上開四管近戰用的爆破釘 —— 擋下來的那一拍可以反擊。 */
    g.add(plate(MAT_P.armor, 0.26, 1.00, 0.92, [0.52, -0.30, 0], 0.08, 0.20, -0.20));
    for (var i = 0; i < 4; i++)
      g.add(mesh(G.cyl, MAT_P.trim, [0.18, 0.32, 0.18],
        [0.60, 0.02 - i * 0.24, 0.30], [Math.PI / 2, 0, 0]));
    g.add(rib(MAT_P.armor, 0.11, 0.11, 0.92, [0.58, 0.16, 0], 0.08, 0.20, -0.20));
    g.add(mesh(G.box, P.glow, [0.05, 0.76, 0.05], [0.66, -0.30, 0.22]));

  } else if (shape === 'pyl-passive') {
    /* 被動莢艙：盾縮小，改掛兩具外置散熱莢艙 —— 剪影上是「背著東西的手臂」。 */
    g.add(plate(MAT_P.armor, 0.22, 0.74, 0.70, [0.50, -0.28, 0], 0.08, 0.20, -0.20));
    g.add(mesh(G.cyl, MAT_P.armor, [0.32, 0.72, 0.32], [0.62, -0.26, 0.16], [0.10, 0, 0.10]));
    g.add(mesh(G.cyl, MAT_P.armor, [0.28, 0.60, 0.28], [0.62, -0.34, -0.22], [0.10, 0, 0.10]));
    g.add(mesh(G.tor, MAT_P.trim, [0.42, 0.42, 0.16], [0.63, -0.08, 0.16], [Math.PI / 2, 0, 0.10]));
    g.add(mesh(G.oct, P.glow, [0.13, 0.13, 0.13], [0.62, 0.12, 0.16]));

  } else {  /* pyl-std：格擋盾。**盾的上緣不可以高過肩線** —— 第二輪拍出來
              盾比機體還高，側面整個剪影只剩一片板子（shots/lancer/）。 */
    g.add(plate(MAT_P.armor, 0.28, 1.16, 1.00, [0.54, -0.32, 0], 0.08, 0.20, -0.20)); // 主盾板
    g.add(plate(MAT_P.armor, 0.20, 0.74, 0.66, [0.68, -0.22, 0.06], 0.08, 0.20, -0.20));// 第二層
    g.add(rib(MAT_P.armor, 0.12, 0.12, 1.00, [0.60, 0.20, 0], 0.08, 0.20, -0.20));     // 上緣亮線
    g.add(seam(0.06, 1.04, 0.10, [0.60, -0.32, 0.36], 0.08, 0.20, -0.20));
    g.add(plate(MAT_P.armor, 0.16, 0.38, 0.50, [0.62, -1.02, 0], 0, 0.20, 0.28));      // 下擺
    g.add(mesh(G.box, P.glow, [0.05, 0.26, 0.05], [0.70, -0.06, 0.18]));
  }
  return g;
}
